import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Sparkles, ShieldCheck, Award, ArrowLeft, Loader2, PartyPopper, Mic, Square, Volume2, VolumeX, Play, TrendingUp } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getCategories, startAssessment, replyAssessment, finishAssessment, ttsSpeak, transcribeAudio } from "@/lib/api";
import { Header, SkillTag, categoryIcon } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TypingDots = () => (
  <div className="flex gap-1.5 items-center px-2 py-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-2 h-2 rounded-full bg-primary/60"
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
      />
    ))}
  </div>
);

const Bubble = ({ role, text, onPlay, speaking }) => {
  const isAi = role === "assistant";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isAi ? "justify-start" : "justify-end"}`}
    >
      <div
        className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
          isAi ? "bg-primary text-primary-foreground rounded-tl-sm" : "bg-secondary text-secondary-foreground rounded-tr-sm"
        }`}
      >
        {isAi && (
          <div className="flex items-center justify-between gap-3 mb-1">
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider opacity-80"><Sparkles className="w-3 h-3" /> SkillBridge AI</span>
            <button
              data-testid="bubble-play-btn"
              onClick={() => onPlay(text)}
              aria-label="Play question audio"
              className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors shrink-0"
            >
              {speaking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
            </button>
          </div>
        )}
        {text}
      </div>
    </motion.div>
  );
};

export default function Assessment() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const retakeCategory = searchParams.get("retake");
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [log, setLog] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [result, setResult] = useState(null);
  const [readAloud, setReadAloud] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [speakingText, setSpeakingText] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const lastSpokenRef = useRef(-1);

  useEffect(() => { getCategories().then(setCategories); }, []);
  const beganRef = useRef(false);
  useEffect(() => {
    if (retakeCategory && !beganRef.current && !category) {
      beganRef.current = true;
      begin(retakeCategory);
    }
    // eslint-disable-next-line
  }, [retakeCategory]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [log, busy]);

  const playTts = async (text) => {
    try {
      if (audioRef.current) { audioRef.current.pause(); }
      setSpeaking(true);
      setSpeakingText(text);
      const url = await ttsSpeak(text);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); setSpeakingText(null); };
      audio.onerror = () => { setSpeaking(false); setSpeakingText(null); };
      await audio.play();
    } catch (e) {
      setSpeaking(false);
      setSpeakingText(null);
      toast.error("Could not play audio");
    }
  };

  // Auto read new AI messages when read-aloud is on
  useEffect(() => {
    if (!readAloud || busy || log.length === 0) return;
    const lastIdx = log.length - 1;
    const last = log[lastIdx];
    if (last.role === "assistant" && lastIdx > lastSpokenRef.current) {
      lastSpokenRef.current = lastIdx;
      playTts(last.message);
    }
    // eslint-disable-next-line
  }, [log, readAloud, busy]);

  const toggleRecord = async () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 500) return;
        setTranscribing(true);
        try {
          const res = await transcribeAudio(blob);
          setInput((prev) => (prev ? prev + " " : "") + (res.text || "").trim());
        } catch (e) {
          toast.error("Could not transcribe audio");
        }
        setTranscribing(false);
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  };

  const begin = async (cat) => {
    setCategory(cat);
    setBusy(true);
    try {
      const res = await startAssessment(currentUser.id, cat);
      setSessionId(res.session_id);
      setLog(res.log);
    } catch (e) {
      toast.error("Could not start assessment");
      setCategory(null);
    }
    setBusy(false);
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput("");
    setLog((l) => [...l, { role: "user", message: msg }]);
    setBusy(true);
    try {
      const res = await replyAssessment(sessionId, msg);
      setLog(res.log);
    } catch (e) {
      toast.error("Message failed. Try again.");
    }
    setBusy(false);
  };

  const finish = async () => {
    setFinishing(true);
    try {
      const res = await finishAssessment(sessionId);
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finish assessment");
    }
    setFinishing(false);
  };

  const userTurns = log.filter((m) => m.role === "user").length;

  // ---------- Category selection ----------
  if (!category) {
    return (
      <div className="min-h-screen grain">
        <Header title="Assessment" />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          <button data-testid="back-btn" onClick={() => navigate("/worker")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <h1 className="font-heading text-3xl font-black tracking-tight">Choose your trade</h1>
          <p className="mt-2 text-foreground/80">Pick the area you want assessed. The AI will ask practical, situational questions.</p>
          <div className="mt-8 grid gap-3">
            {categories.map((c, i) => (
              <motion.button
                key={c}
                data-testid={`category-${c.replace(/\s+/g, "-").toLowerCase()}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => begin(c)}
                disabled={busy}
                className="w-full rounded-lg border border-border bg-card p-5 flex items-center gap-4 hover:border-primary transition-colors text-left disabled:opacity-60"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  {categoryIcon(c, "w-6 h-6")}
                </div>
                <div className="flex-1">
                  <div className="font-heading font-bold text-lg">{c}</div>
                  <div className="text-sm text-muted-foreground">Get verified in {c.toLowerCase()} skills</div>
                </div>
                {busy && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Results ----------
  if (result) {
    return (
      <div className="min-h-screen grain">
        <Header title="Assessment" />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <PartyPopper className="w-10 h-10" />
            </div>
            <h1 className="mt-4 font-heading text-3xl font-black tracking-tight">Credential Earned!</h1>
            <p className="mt-2 text-foreground/80">{result.evaluation_summary}</p>
          </motion.div>

          <div className="mt-8 rounded-xl bg-primary text-primary-foreground p-6 text-center" data-testid="earned-badge">
            <Award className="w-10 h-10 mx-auto" />
            <div className="mt-2 font-heading text-xl font-black">{result.badge.badge_title}</div>
            <div className="font-mono text-xs opacity-80 mt-1">Verify #{result.badge.public_verify_hash}</div>
          </div>

          <h2 className="mt-8 font-heading text-lg font-bold">Newly Verified Skills</h2>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {result.skills.map((s) => <SkillTag key={s.id} skill={s} testid={`result-skill-${s.id}`} />)}
          </div>

          {result.upgraded_skills?.length > 0 && (
            <div data-testid="upgraded-callout" className="mt-6 rounded-lg bg-primary/10 border border-primary/20 p-4">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <TrendingUp className="w-4 h-4" /> Level-up!
              </div>
              <p className="mt-1 text-sm text-foreground/80">
                You raised your proficiency in: <b>{result.upgraded_skills.join(", ")}</b>.
              </p>
            </div>
          )}

          <div className="mt-8 flex gap-3">
            <Button data-testid="result-dashboard-btn" className="flex-1" onClick={() => navigate("/worker")}>Back to Dashboard</Button>
            <Button data-testid="result-resume-btn" variant="outline" className="flex-1" onClick={() => navigate(`/resume/${currentUser.id}`)}>View Resume</Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Chat ----------
  return (
    <div className="min-h-screen flex flex-col">
      <Header title={category} />
      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col px-4 sm:px-6">
        <div className="flex items-center justify-between py-4">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
            {categoryIcon(category)} {category} Interview
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="read-aloud-toggle"
              onClick={() => setReadAloud((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${readAloud ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}
            >
              {readAloud ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              Read aloud
            </button>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{userTurns} answer{userTurns === 1 ? "" : "s"}</span>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pb-4">
          <AnimatePresence initial={false}>
            {log.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.message} onPlay={playTts} speaking={speaking && speakingText === m.message} />
            ))}
          </AnimatePresence>
          {busy && (
            <div className="flex justify-start">
              <div className="bg-primary rounded-lg rounded-tl-sm"><TypingDots /></div>
            </div>
          )}
        </div>

        {userTurns >= 2 && (
          <div className="py-2">
            <Button
              data-testid="finish-assessment-btn"
              onClick={finish}
              disabled={finishing}
              className="w-full"
              variant="secondary"
            >
              {finishing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Evaluating your skills…</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Finish & Get My Credentials</>}
            </Button>
          </div>
        )}

        <div className="sticky bottom-0 bg-background/80 backdrop-blur-xl py-3 border-t border-border">
          <div className="flex gap-2">
            <Input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={recording ? "Listening… tap stop when done" : (transcribing ? "Transcribing your answer…" : "Type or speak your answer…")}
              disabled={busy || finishing || recording || transcribing}
              className="flex-1"
            />
            <Button
              data-testid="mic-btn"
              onClick={toggleRecord}
              disabled={busy || finishing || transcribing}
              size="icon"
              variant={recording ? "destructive" : "outline"}
              aria-label={recording ? "Stop recording" : "Record answer"}
              className="shrink-0 w-11 h-11"
            >
              {transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : recording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </Button>
            <Button data-testid="chat-send-btn" onClick={send} disabled={busy || finishing || !input.trim()} size="icon" className="shrink-0 w-11 h-11">
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
